import { api } from '../utils/apiClient';

export type PostStatus = 'pending' | 'read' | 'reply_generated' | 'ignored' | 'saved_for_later';

export interface GeneratedReply {
  reply_text: string;
  generated_at: string;
  brand_info?: string;
  tone_preference?: string;
  parent_comment_id?: string;  // Reddit comment ID if replying to a specific comment

  // Engagement tracking populated after the user marks this reply as posted
  reddit_comment_id?: string;
  reddit_comment_permalink?: string;
  posted_at?: string;
  last_engagement_check_at?: string;
  engagement_score?: number;
  engagement_replies_count?: number;
  engagement_status?: 'draft' | 'posted' | 'deleted' | 'unknown';
}

// Nested comment types for comment tree display
export interface NestedComment {
  id: string;
  body: string;
  author: string;
  score: number;
  created_utc: number;
  depth: number;
  parent_id: string | null;
  is_submitter: boolean;
  is_deleted: boolean;
  children: NestedComment[];
}

export interface CommentsResponse {
  comments: NestedComment[];
  metadata: {
    post_id: string;
    post_title: string;
    post_author: string | null;
    total_comments: number;
    top_level_count: number;
    sort_by: string;
  };
}

export interface PostWithReply {
  id: string;
  user_id: string;
  brand_id?: string;  // Brand this post belongs to
  monitored_subreddit_id: string;
  scrape_history_id: string;
  
  // Post information
  post_title: string;
  post_url: string;
  post_author: string;
  post_score: number;
  post_num_comments: number;
  post_created_utc: string;
  subreddit: string;
  post_permalink: string;
  post_selftext?: string;
  matched_keywords: string[];
  
  // Subreddit metrics
  subreddit_subscribers?: number;
  subreddit_active_users?: number;  // Currently online/active users
  
  // Geography
  geography?: string;  // Country code like "IN", "US" or "global"
  
  // Reply information (optional for posts that are ignored or saved for later)
  generated_reply?: GeneratedReply;  // Deprecated - for backward compatibility
  generated_replies: GeneratedReply[];  // New format - array of replies
  
  // Status tracking
  post_status: PostStatus;
  
  // Tags and custom fields
  tags: string[];
  custom_status?: string;
  
  // Relevance scoring (computed server-side when sorting by relevance_desc)
  relevance_score?: number;  // 0-100 opportunity score
  relevance_rank?: number;   // Global position when sorted by relevance
  
  // Metadata
  created_at: string;
  updated_at: string;
}

export interface PostStatusResponse {
  has_reply: boolean;
  post_id?: string;
  post_status?: PostStatus;
}

export interface GenerateReplyRequest {
  post_title: string;
  post_url: string;
  post_author: string;
  post_score: number;
  post_num_comments: number;
  post_created_utc: string;
  subreddit: string;
  post_permalink: string;
  post_selftext?: string;
  matched_keywords: string[];
  monitored_subreddit_id: string;
  scrape_history_id: string;
  keywords: string[];
  brand_info?: string;
  custom_wording?: string;
  tone_preference?: string;
}

export interface UpdatePostStatusRequest {
  post_permalink: string;
  post_status: PostStatus;
  monitored_subreddit_id: string;
  scrape_history_id: string;
  post_title: string;
  post_url: string;
  post_author: string;
  post_score: number;
  post_num_comments: number;
  post_created_utc: string;
  subreddit: string;
  post_selftext?: string;
  matched_keywords: string[];
}

export type SortOption = 
  | 'date_desc' 
  | 'date_asc' 
  | 'score_desc' 
  | 'score_asc' 
  | 'comments_desc' 
  | 'comments_asc' 
  | 'title_asc' 
  | 'title_desc'
  | 'relevance_desc';  // Best opportunities first (client-side scoring)

export interface FilterOptions {
  brand_id?: string;  // Filter by brand
  status_filter?: string;
  search_query?: string;
  tags?: string[];
  custom_status?: string;
  subreddit?: string;
  date_from?: string;
  date_to?: string;
  min_score?: number;
  max_score?: number;
  min_comments?: number;
  max_comments?: number;
  // New filters for subreddit metrics and geography
  min_subreddit_active_users?: number;  // Filter by minimum active users (weekly visitors proxy)
  geography?: string;  // Filter by geography: "global" or country code like "IN", "US"
  sort_by?: SortOption;
  limit?: number;
  skip?: number;
}

export interface UpdatePostTagsRequest {
  post_id: string;
  tags: string[];
}

export interface UpdatePostCustomStatusRequest {
  post_id: string;
  custom_status?: string;
}

export interface TagInfo {
  tag: string;
  count: number;
}

export interface SubredditInfo {
  subreddit: string;
  count: number;
}

export interface MarkReplyPostedResponse {
  post_id: string;
  reply_index: number;
  reddit_comment_id: string;
  reddit_comment_permalink: string;
  posted_at: string;
  engagement?: GeneratedReply | null;
}

export interface AutoDetectReplyResponse {
  matched: boolean;
  similarity?: number | null;
  reddit_comment_id?: string | null;
  reddit_comment_permalink?: string | null;
  posted_at?: string | null;
  engagement?: GeneratedReply | null;
}

export const replyPostsService = {
  async markReplyPosted(
    postId: string,
    replyIndex: number,
    redditCommentUrl: string,
  ): Promise<MarkReplyPostedResponse> {
    return api.post<MarkReplyPostedResponse>(
      `/api/reply-posts/${encodeURIComponent(postId)}/generated-replies/${replyIndex}/mark-posted`,
      { reddit_comment_url: redditCommentUrl },
    );
  },

  async autoDetectPostedReply(
    postId: string,
    replyIndex: number,
  ): Promise<AutoDetectReplyResponse> {
    return api.post<AutoDetectReplyResponse>(
      `/api/reply-posts/${encodeURIComponent(postId)}/generated-replies/${replyIndex}/auto-detect`,
    );
  },

  async generateReply(request: GenerateReplyRequest): Promise<PostWithReply> {
    // Step 1: Generate the AI reply using the new AI reply service
    const aiReply = await api.post<any>('/api/ai-reply/generate', {
      post_content: request.post_selftext || '',
      post_title: request.post_title,
      brand_info: request.brand_info,
      custom_wording: request.custom_wording,
      tone_preference: request.tone_preference || 'neutral'
    });
    
    // Step 2: Save the generated reply to the database
    return api.post<PostWithReply>('/api/reply-posts/save-generated-reply', {
      post_permalink: request.post_permalink,
      post_status: 'reply_generated',
      monitored_subreddit_id: request.monitored_subreddit_id,
      scrape_history_id: request.scrape_history_id,
      post_title: request.post_title,
      post_url: request.post_url,
      post_author: request.post_author,
      post_score: request.post_score,
      post_num_comments: request.post_num_comments,
      post_created_utc: request.post_created_utc,
      subreddit: request.subreddit,
      post_selftext: request.post_selftext,
      matched_keywords: request.matched_keywords,
      reply_text: aiReply.final_reply
    });
  },

  async saveGeneratedReply(request: GenerateReplyRequest, replyText: string, metadata?: {brand_info?: string; tone_preference?: string; parent_comment_id?: string}): Promise<PostWithReply> {
    return api.post<PostWithReply>('/api/reply-posts/save-generated-reply', {
      post_permalink: request.post_permalink,
      post_status: 'reply_generated',
      monitored_subreddit_id: request.monitored_subreddit_id,
      scrape_history_id: request.scrape_history_id,
      post_title: request.post_title,
      post_url: request.post_url,
      post_author: request.post_author,
      post_score: request.post_score,
      post_num_comments: request.post_num_comments,
      post_created_utc: request.post_created_utc,
      subreddit: request.subreddit,
      post_selftext: request.post_selftext,
      matched_keywords: request.matched_keywords,
      reply_text: replyText,
      brand_info: metadata?.brand_info,
      tone_preference: metadata?.tone_preference,
      parent_comment_id: metadata?.parent_comment_id
    });
  },
  
  async getPostsWithReplies(
    limit: number = 50, 
    skip: number = 0,
    statusFilter?: string,
    searchQuery?: string,
    brandId?: string
  ): Promise<PostWithReply[]> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      skip: skip.toString()
    });
    
    if (brandId) {
      params.append('brand_id', brandId);
    }
    
    if (statusFilter) {
      params.append('status_filter', statusFilter);
    }
    
    if (searchQuery) {
      params.append('search_query', searchQuery);
    }
    
    return api.get<PostWithReply[]>(`/api/reply-posts/posts-with-replies?${params.toString()}`);
  },

  async getPostsWithRepliesFiltered(options: FilterOptions): Promise<PostWithReply[]> {
    const params = new URLSearchParams({
      limit: (options.limit || 50).toString(),
      skip: (options.skip || 0).toString()
    });
    
    console.log('[replyPostsService] getPostsWithRepliesFiltered called with:', {
      sort_by: options.sort_by,
      skip: options.skip,
      limit: options.limit,
      brand_id: options.brand_id
    });
    
    if (options.brand_id) {
      params.append('brand_id', options.brand_id);
    }
    
    if (options.status_filter) {
      params.append('status_filter', options.status_filter);
    }
    
    if (options.search_query) {
      params.append('search_query', options.search_query);
    }
    
    if (options.tags && options.tags.length > 0) {
      params.append('tags', options.tags.join(','));
    }
    
    if (options.custom_status) {
      params.append('custom_status', options.custom_status);
    }
    
    if (options.subreddit) {
      params.append('subreddit', options.subreddit);
    }
    
    if (options.date_from) {
      params.append('date_from', options.date_from);
    }
    
    if (options.date_to) {
      params.append('date_to', options.date_to);
    }
    
    if (options.min_score !== undefined) {
      params.append('min_score', options.min_score.toString());
    }
    
    if (options.max_score !== undefined) {
      params.append('max_score', options.max_score.toString());
    }
    
    if (options.min_comments !== undefined) {
      params.append('min_comments', options.min_comments.toString());
    }
    
    if (options.max_comments !== undefined) {
      params.append('max_comments', options.max_comments.toString());
    }
    
    // New filters for subreddit metrics and geography
    if (options.min_subreddit_active_users !== undefined) {
      params.append('min_subreddit_active_users', options.min_subreddit_active_users.toString());
    }
    
    if (options.geography) {
      params.append('geography', options.geography);
    }
    
    if (options.sort_by) {
      params.append('sort_by', options.sort_by);
    }
    
    return api.get<PostWithReply[]>(`/api/reply-posts/posts-with-replies?${params.toString()}`);
  },
  
  async deletePostWithReply(postId: string): Promise<void> {
    await api.delete(`/api/reply-posts/posts-with-replies/${postId}`);
  },
  
  async getPostsWithRepliesCount(): Promise<{ count: number }> {
    return api.get<{ count: number }>('/api/reply-posts/posts-with-replies/count');
  },

  async checkPostStatus(postPermalink: string): Promise<PostStatusResponse> {
    const encodedPermalink = encodeURIComponent(postPermalink);
    return api.get<PostStatusResponse>(`/api/reply-posts/check-post-status/${encodedPermalink}`);
  },

  async updatePostStatus(request: UpdatePostStatusRequest): Promise<{ message: string; post_id: string }> {
    return api.post<{ message: string; post_id: string }>('/api/reply-posts/update-post-status', request);
  },

  async updatePostTags(request: UpdatePostTagsRequest): Promise<{ message: string }> {
    return api.put<{ message: string }>(
      `/api/reply-posts/posts-with-replies/${request.post_id}/tags`,
      { tags: request.tags }
    );
  },

  async updatePostCustomStatus(request: UpdatePostCustomStatusRequest): Promise<{ message: string }> {
    return api.put<{ message: string }>(
      `/api/reply-posts/posts-with-replies/${request.post_id}/custom-status`,
      { custom_status: request.custom_status }
    );
  },

  async getAvailableTags(): Promise<{ tags: TagInfo[] }> {
    return api.get<{ tags: TagInfo[] }>('/api/reply-posts/posts-with-replies/tags');
  },

  async getAvailableSubreddits(): Promise<{ subreddits: SubredditInfo[] }> {
    return api.get<{ subreddits: SubredditInfo[] }>('/api/reply-posts/posts-with-replies/subreddits');
  },

  async getPostsWithRepliesPaginated(options: FilterOptions): Promise<{ posts: PostWithReply[]; total: number }> {
    // Get posts
    const posts = await this.getPostsWithRepliesFiltered(options);
    
    // Get total count with same filters
    const countParams = new URLSearchParams();
    
    if (options.brand_id) {
      countParams.append('brand_id', options.brand_id);
    }
    
    if (options.status_filter) {
      countParams.append('status_filter', options.status_filter);
    }
    
    if (options.search_query) {
      countParams.append('search_query', options.search_query);
    }
    
    if (options.tags && options.tags.length > 0) {
      countParams.append('tags', options.tags.join(','));
    }
    
    if (options.custom_status) {
      countParams.append('custom_status', options.custom_status);
    }
    
    if (options.subreddit) {
      countParams.append('subreddit', options.subreddit);
    }
    
    if (options.date_from) {
      countParams.append('date_from', options.date_from);
    }
    
    if (options.date_to) {
      countParams.append('date_to', options.date_to);
    }
    
    if (options.min_score !== undefined) {
      countParams.append('min_score', options.min_score.toString());
    }
    
    if (options.max_score !== undefined) {
      countParams.append('max_score', options.max_score.toString());
    }
    
    if (options.min_comments !== undefined) {
      countParams.append('min_comments', options.min_comments.toString());
    }
    
    if (options.max_comments !== undefined) {
      countParams.append('max_comments', options.max_comments.toString());
    }
    
    // New filters for subreddit metrics and geography
    if (options.min_subreddit_active_users !== undefined) {
      countParams.append('min_subreddit_active_users', options.min_subreddit_active_users.toString());
    }
    
    if (options.geography) {
      countParams.append('geography', options.geography);
    }
    
    const countData = await api.get<{ count: number }>(
      `/api/reply-posts/posts-with-replies/count?${countParams.toString()}`
    );
    
    return {
      posts,
      total: countData.count
    };
  },

  async refreshPost(postId: string): Promise<PostWithReply> {
    return api.post<PostWithReply>(`/api/reply-posts/refresh-post/${postId}`);
  },

  /**
   * Fetch all comments for a post in nested/threaded format
   * Comments are fetched fresh from Reddit API
   */
  async getPostComments(postId: string, sortBy: string = 'best'): Promise<CommentsResponse> {
    const params = new URLSearchParams({
      sort_by: sortBy
    });
    
    return api.get<CommentsResponse>(
      `/api/reply-posts/posts-with-replies/${postId}/comments?${params.toString()}`
    );
  }
};

